import { v4 as uuidv4 } from 'uuid';
import Message from './Message';

/**
 * Modelo que representa una conversación de chat
 */
class Chat {
  /**
   * Crea una instancia de Chat
   * @param {Object} config - Configuración del chat
   * @param {string} config.id - Identificador único del chat
   * @param {string} config.agentKey - Clave del agente asociado al chat
   * @param {string} [config.sessionId] - Identificador único de la sesión
   * @param {string} [config.title] - Título del chat
   * @param {Array<Object>} [config.messages] - Lista de mensajes del chat
   * @param {Date|string} [config.createdAt] - Fecha de creación del chat
   * @param {Date|string} [config.updatedAt] - Fecha de última actualización del chat
   */
  constructor({ 
    id, 
    agentKey, 
    sessionId,
    title = 'Nuevo chat', 
    messages = [], 
    createdAt = new Date(),
    updatedAt = new Date() 
  }) {
    this.id = id || `chat_${uuidv4()}`;
    this.agentKey = agentKey;
    this.sessionId = sessionId || `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.title = title;
    this.messages = messages.map(msg => 
      msg instanceof Message ? msg : Message.fromObject(msg)
    );
    this.createdAt = createdAt instanceof Date ? new Date(createdAt) : new Date(createdAt || Date.now());
    this.updatedAt = updatedAt instanceof Date ? new Date(updatedAt) : new Date(updatedAt || Date.now());
  }

  /**
   * Crea una instancia de Chat desde un objeto plano
   * @param {Object} data - Datos del chat
   * @returns {Chat} Instancia de Chat
   */
  static fromObject(data) {
    return new Chat({
      id: data.id,
      agentKey: data.agentKey,
      sessionId: data.sessionId,
      title: data.title,
      messages: data.messages || [],
      createdAt: data.createdAt,
      updatedAt: data.updatedAt || data.createdAt,
    });
  }

  /**
   * Convierte la instancia a un objeto plano
   * @returns {Object} Objeto plano con las propiedades del chat
   */
  toObject() {
    return {
      id: this.id,
      agentKey: this.agentKey,
      sessionId: this.sessionId,
      title: this.title,
      messages: this.messages.map(msg => 
        msg instanceof Message ? msg.toObject() : msg
      ),
      createdAt: this.createdAt.toISOString(),
      updatedAt: this.updatedAt.toISOString(),
    };
  }

  /**
   * Agrega un mensaje al chat
   * @param {Message} message - Mensaje a agregar
   * @returns {Chat} Nueva instancia de Chat con el mensaje agregado
   */
  addMessage(message) {
    const newMessage = message instanceof Message 
      ? message 
      : Message.fromObject(message);
    
    return new Chat({
      ...this.toObject(),
      messages: [...this.messages, newMessage],
      updatedAt: new Date(),
    });
  }

  /**
   * Obtiene el último mensaje del chat
   * @returns {Message|undefined} Último mensaje o undefined si no hay mensajes
   */
  getLastMessage() {
    if (this.messages.length === 0) return undefined;
    return this.messages[this.messages.length - 1];
  }

  /**
   * Actualiza el título del chat basado en el primer mensaje
   * @param {string} [fallbackTitle] - Título a usar si no hay mensajes
   * @returns {Chat} Nueva instancia de Chat con el título actualizado
   */
  updateTitle(fallbackTitle = 'Nuevo chat') {
    if (this.title && this.title !== 'Nuevo chat') {
      return this; // No actualizar si ya tiene un título personalizado
    }

    const firstMessage = this.messages[0];
    let newTitle = fallbackTitle;

    if (firstMessage && firstMessage.text) {
      // Tomar las primeras 30 caracteres del primer mensaje como título
      newTitle = firstMessage.text.trim().substring(0, 30);
      if (firstMessage.text.length > 30) newTitle += '...';
    }

    return new Chat({
      ...this.toObject(),
      title: newTitle,
    });
  }
}

export default Chat;
